import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_06 } from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_VALUE_ZERO,
  CON_MAX_NAME_LENGTH,
  CON_MAX_DESCRIPTION_LENGTH,
} from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import { 
  PackageMessages, 
  UITextMessages,
  getFieldMaxLengthMessage
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface PackageItem {
  packagecode?: string | number;
  packagename?: string;
  descrip?: string;
}

interface PackageDetails {
  packagename: string;
  descrip: string;
  packagecode: string | number;
}

interface ErrorState {
  packagename?: string;
  descrip?: string;
  [key: string]: string | undefined;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  RESTCODE?: string;
  USERNAME?: string;
  COMPCODE?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: PackageItem;
  actionText?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function CreatePackages(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const location = useLocation();
  const { selectedItem } = (location.state as LocationState) || {};
  const isEdit = (location.state as LocationState)?.actionText === "Edit";

  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { STAFFMAPLOCODE, USERNAME, COMPCODE } = SYS_USER.USER_PARAM || {};

  const [data, setData] = useState<PackageItem[]>([]);
  const [error, setError] = useState<ErrorState>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [selectedItemDetails, setSelectedItemDetails] = useState<PackageDetails>({
    packagename: "",
    descrip: "",
    packagecode: CON_VALUE_ZERO,
  });

  useEffect(() => {
    if (selectedItem) {
      setSelectedItemDetails({
        packagename: selectedItem.packagename || "",
        descrip: selectedItem.descrip || "",
        packagecode: selectedItem.packagecode || CON_VALUE_ZERO,
      });
    }
  }, [selectedItem]);

  useEffect(() => {
    setPageData({ title: UITextMessages.breadcrumb.package });
  }, [setPageData]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_06, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });
        setData((result as PackageItem[]) || []);
      } catch (err) {
        console.error(err);
        setAlertType("error");
        setAlertMessage(PackageMessages.error.fetchFailed);
        setAlertOpen(true);
      }
    };
    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = value.replace(/^\s+/, "");
    let errorMsg = "";
    if (trimmedValue.length === CON_MAX_NAME_LENGTH) {
      errorMsg = getFieldMaxLengthMessage(UITextMessages.label.packageName, CON_MAX_NAME_LENGTH);
    }
    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setSelectedItemDetails((prev) => ({ ...prev, [name]: trimmedValue }));
  };

  const handleInputChangedes = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = value;
    let errorMsg = "";
    if (trimmedValue.length === CON_MAX_DESCRIPTION_LENGTH) {
      errorMsg = getFieldMaxLengthMessage(UITextMessages.label.description, CON_MAX_DESCRIPTION_LENGTH);
    }
    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setSelectedItemDetails((prev) => ({ ...prev, [name]: trimmedValue }));
  };

  const handleCancel = (): void => {
    // Clear any error messages and navigate back to the previous page
    setError({});
    navigate("/vCreatePackages");
  };

  const goBackToView = (): void => {
    navigate("/vCreatePackages");
  };

  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);

    const { packagename, descrip } = selectedItemDetails;
    const trimmedPackage = (packagename || "").trim();
    const trimmedDesc = (descrip || "").trim();

    let validationErrors: ErrorState = {};
    let hasError = false;

    if (!trimmedPackage) {
      validationErrors.packagename = PackageMessages.warning.nameRequired;
      hasError = true;
    }

    setError(validationErrors);

    if (hasError) {
      setAlertType("error");
      setAlertMessage(PackageMessages.warning.requiredFields);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    const isDuplicate = data?.some(
      (item) =>
        item.packagename?.toLowerCase().trim() ===
          trimmedPackage.toLowerCase() &&
        item.packagecode !== selectedItemDetails.packagecode
    );
    
    if (isDuplicate) {
      setAlertType("warning");
      setAlertMessage(PackageMessages.warning.alreadyExists);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    const payload = {
      packagename: trimmedPackage,
      descrip: trimmedDesc,
      packagecode: selectedItemDetails.packagecode,
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME,
    };

    try {
      await externalpostApi(SETTING_API_06, payload);
      setAlertType("success");
      setAlertMessage(
        isEdit ? PackageMessages.success.update : PackageMessages.success.create
      );
      setAlertOpen(true);
      setTimeout(() => navigate("/vCreatePackages"), 2000);
    } catch (err) {
      const error = err as ApiError;
      console.error("API Error:", error);
      setAlertType("error");
      setAlertMessage(error.response?.data?.message || PackageMessages.error.unexpected);
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  /* ================= UI ================= */
  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title={UITextMessages.tooltip.back} arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="icon_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            {UITextMessages.breadcrumb.home}
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            {UITextMessages.breadcrumb.settings}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            {UITextMessages.breadcrumb.package}
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 8, sm: 10, md: 10, lg: 10, xl: 10 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {isEdit ? UITextMessages.pageTitle.editPackage : UITextMessages.pageTitle.createPackage}
              </Typography>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.add_form_page_container}>
          <Grid container spacing={2}>
            {/* ===== PACKAGE NAME ===== */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="txt_packagename"
                label={UITextMessages.label.packageName}
                variant="outlined"
                fullWidth
                size="small"
                required
                name="packagename"
                value={selectedItemDetails.packagename}
                onChange={handleInputChange}
                error={!!error.packagename}
                helperText={error.packagename}
                inputProps={{ maxLength: CON_MAX_NAME_LENGTH }}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} />

            {/* ===== DESCRIPTION (Optional) ===== */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="txt_descrip"
                label={UITextMessages.label.description}
                variant="outlined"
                fullWidth
                size="small"
                name="descrip"
                value={selectedItemDetails.descrip}
                onChange={handleInputChangedes}
                error={!!error.descrip}
                helperText={error.descrip}
                inputProps={{ maxLength: CON_MAX_DESCRIPTION_LENGTH }}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_cancel"
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
          >
            {UITextMessages.button.cancel || "Cancel"}
          </Button>

          <Button
            id="btn_confirm/modify"
            variant="contained"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            disabled={isButtonDisabled}
            onClick={handleConfirmClick}
          >
            {isEdit ? UITextMessages.button.modify : UITextMessages.button.confirm}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreatePackages;